import React, { useState } from "react";

import { CreditCard, Loader2 } from "lucide-react";

import { Button } from "~/components/ui/button";
import {
  Sheet,
  SheetContent,
  SheetDescription,
  SheetHeader,
  SheetTitle,
  SheetTrigger,
} from "~/components/ui/sheet";
import { toast } from "~/hooks/use-toast";
import { supabase } from "~/integrations/supabase/client";
import { ClientInfo } from "~/pages/purchase-orders/hooks/utils/clientUtils";
import { useProfileStore } from "~/store/profileStore";
import { SalesRequest } from "~/types/client";
import { grossFromNet } from "~/utils/iva";

interface CreditPaymentActionsProps {
  selectedOrder: SalesRequest;
  clientInfo?: ClientInfo;
  onOrderStatusChange?: () => void;
}

export const CreditPaymentActions: React.FC<CreditPaymentActionsProps> = ({
  selectedOrder,
  clientInfo,
  onOrderStatusChange,
}) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSheetOpen, setIsSheetOpen] = useState(false);
  const profileStore = useProfileStore();

  const isSeller = profileStore.role === "seller";
  const isInTransit = selectedOrder.status === "in_transit";
  const isDelivered = selectedOrder.status === "delivered";

  // Check if client has credit condition
  const hasCreditCondition =
    clientInfo?.salesConditionType === "credit_line" ||
    clientInfo?.salesConditionType === "external_platform";

  // Check if order is unpaid
  const isUnpaid = !selectedOrder.paid;

  // Show for sellers when order is in in_transit or delivered status,
  // client has credit condition, and order is unpaid
  if (
    !isSeller ||
    !hasCreditCondition ||
    !isUnpaid ||
    (!isInTransit && !isDelivered)
  ) {
    return null;
  }

  const handleRegisterPayment = async () => {
    setIsSubmitting(true);
    try {
      // Update status to "payment_to_check" for validation by cobranza
      const { error } = await supabase
        .from("sales_request")
        .update({
          status: "payment_to_check",
          updated_at: new Date().toISOString(),
        })
        .eq("id", selectedOrder.id);

      if (error) throw error;

      // Trigger logs the change; just attach the reason to that row.
      await supabase.rpc("annotate_latest_status_change", {
        p_sales_request_id: selectedOrder.id,
        p_new_status: "payment_to_check",
        p_comment:
          "Pago registrado por vendedor para cliente con condición de crédito",
      });

      toast({
        title: "Pago registrado",
        description:
          "El pago ha sido registrado. Pendiente de validación por cobranza.",
      });

      setIsSheetOpen(false);
      if (onOrderStatusChange) onOrderStatusChange();
    } catch (error) {
      console.error("Error registering payment:", error);
      toast({
        title: "Error",
        description: "No se pudo registrar el pago",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  const getStatusLabel = (status: string) => {
    const statusLabels = {
      in_transit: "En Tránsito",
      delivered: "Entregado",
    };
    return statusLabels[status as keyof typeof statusLabels] || status;
  };

  return (
    <Sheet open={isSheetOpen} onOpenChange={setIsSheetOpen}>
      <SheetTrigger asChild>
        <Button
          variant="outline"
          className="flex items-center border-blue-200 text-blue-600 hover:bg-blue-50 hover:text-blue-700"
        >
          <CreditCard className="mr-2 h-4 w-4" />
          Registrar Pago
        </Button>
      </SheetTrigger>
      <SheetContent>
        <SheetHeader>
          <SheetTitle>Registrar Pago</SheetTitle>
          <SheetDescription>
            Registrar pago para nota de venta en estado "
            {getStatusLabel(selectedOrder.status)}"
          </SheetDescription>
        </SheetHeader>

        <div className="mt-6 space-y-4">
          <div className="rounded-lg border bg-slate-50 p-4">
            <h4 className="mb-2 font-medium">Detalles de la Nota de Venta</h4>
            <div className="space-y-2 text-sm">
              <div className="flex justify-between">
                <span className="text-muted-foreground">Estado:</span>
                <span className="font-medium">
                  {getStatusLabel(selectedOrder.status)}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">Monto Total:</span>
                <span className="font-medium">
                  $
                  {grossFromNet(
                    selectedOrder.totalAmount,
                    selectedOrder.isExento
                  ).toLocaleString("es-CL")}
                </span>
              </div>
              <div className="flex justify-between">
                <span className="text-muted-foreground">
                  Condición de Venta:
                </span>
                <span className="font-medium">
                  {clientInfo?.salesConditionType === "credit_line"
                    ? "Línea de Crédito"
                    : "Plataforma Externa"}
                </span>
              </div>
            </div>
          </div>

          <div className="rounded-lg border border-amber-200 bg-amber-50 p-4">
            <h4 className="mb-2 font-medium text-amber-800">
              Información Importante
            </h4>
            <p className="text-sm text-amber-700">
              Al registrar este pago, la nota de venta será enviada a validación
              por el equipo de cobranza. El pago será verificado antes de ser
              confirmado.
            </p>
          </div>

          <div className="flex gap-3">
            <Button
              variant="outline"
              onClick={() => setIsSheetOpen(false)}
              className="flex-1"
            >
              Cancelar
            </Button>
            <Button
              onClick={handleRegisterPayment}
              disabled={isSubmitting}
              className="flex-1"
            >
              {isSubmitting ? (
                <>
                  <Loader2 className="mr-2 h-4 w-4 animate-spin" />
                  Registrando...
                </>
              ) : (
                "Confirmar Pago"
              )}
            </Button>
          </div>
        </div>
      </SheetContent>
    </Sheet>
  );
};
